import React, { useMemo, useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "../../utils/cn";
import { laneOptions, laneColors } from "../../utils/constants";
import { SortableEpicCard } from "../cards/SortableEpicCard";
import { Epic, Project, EpicLane, User, UserRole } from "../../types";

interface KanbanLaneProps {
    lane: EpicLane;
    items: Epic[];
    selectedId: string | null;
    onSelect: (id: string) => void;
    getPreviousScore: (id: string) => number | undefined | null;
    getProjectName?: (id?: string) => string;
    projects?: Project[];
    onUpdate?: (id: string, updates: Partial<Epic>) => void;
    users?: User[];
    userRole?: UserRole;
}

function KanbanLane({ lane, items, selectedId, onSelect, getPreviousScore, getProjectName, projects, onUpdate, users, userRole }: KanbanLaneProps) {
    const { setNodeRef, isOver } = useDroppable({
        id: lane,
        data: { type: 'lane', lane }
    });

    return (
        <div
            className="flex-shrink-0 w-full md:w-auto md:flex-1 min-w-[280px] flex flex-col h-full bg-slate-100/50 dark:bg-slate-900/40 rounded-2xl md:rounded-3xl border border-black/5 dark:border-white/5 backdrop-blur-3xl snap-center md:snap-align-none"
            style={{ minHeight: '500px' }}
        >
            <div className="p-3 md:p-4 border-b border-black/5 dark:border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className={cn("w-2 h-2 rounded-full", laneColors[lane].dot)} />
                    <h3 className="text-xs font-black uppercase tracking-widest text-slate-700 dark:text-slate-300">{lane}</h3>
                </div>
                <span className="bg-white dark:bg-slate-800 px-2 py-0.5 rounded-full text-[10px] font-black">{items.length}</span>
            </div>
            {/* This is the droppable area - the entire scrollable content */}
            <div
                ref={setNodeRef}
                data-lane={lane}
                className={cn(
                    "flex-1 overflow-y-auto p-2 md:p-3 space-y-2 md:space-y-3 custom-scrollbar transition-colors duration-150",
                    isOver && "bg-cyan-500/20"
                )}
                style={{ minHeight: '400px' }}
            >
                <SortableContext
                    id={lane}
                    items={items.map(e => e.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {items.map(epic => (
                        <SortableEpicCard
                            key={epic.id}
                            epic={epic}
                            selectedId={selectedId}
                            onSelect={onSelect}
                            getPreviousScore={getPreviousScore}
                            getProjectName={getProjectName}
                            projects={projects}
                            onUpdate={onUpdate}
                            users={users}
                            userRole={userRole}
                        />
                    ))}
                </SortableContext>
                {items.length === 0 && (
                    <div
                        className="p-6 md:p-10 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl md:rounded-2xl flex items-center justify-center opacity-30"
                        style={{ pointerEvents: 'none' }}
                    >
                        <span className="text-[9px] md:text-[10px] font-bold uppercase">Empty Lane</span>
                    </div>
                )}
            </div>
        </div>
    );
}

interface KanbanViewProps {
    epics: Epic[];
    selectedId: string | null;
    onSelect: (id: string) => void;
    getPreviousScore: (id: string) => number | undefined | null;
    getProjectName?: (id?: string) => string;
    projects?: Project[];
    onUpdate?: (id: string, updates: Partial<Epic>) => void;
    users?: User[];
    userRole?: UserRole;
}

export function KanbanView({
    epics,
    selectedId,
    onSelect,
    getPreviousScore,
    getProjectName,
    projects,
    onUpdate,
    users,
    userRole
}: KanbanViewProps) {
    // Hidden lanes state - Unassigned and Done hidden by default
    const [hiddenLanes, setHiddenLanes] = useState<Set<string>>(new Set(['Unassigned', 'Done']));

    const lanes = useMemo(() => {
        const groups: Record<string, Epic[]> = { Unassigned: [], Now: [], Next: [], Later: [], Done: [] };
        epics.forEach(e => {
            if (groups[e.lane]) groups[e.lane].push(e);
        });
        return groups;
    }, [epics]);

    const visibleLanes = useMemo(() => {
        let baseLanes = [...laneOptions];
        if (userRole === 'owner') {
            baseLanes = baseLanes.filter(l => l !== 'Unassigned');
        }
        return baseLanes.filter(l => !hiddenLanes.has(l));
    }, [userRole, hiddenLanes]);

    const toggleLane = (lane: string) => {
        setHiddenLanes(prev => {
            const next = new Set(prev);
            if (next.has(lane)) {
                next.delete(lane);
            } else {
                next.add(lane);
            }
            return next;
        });
    };

    // Lanes that can be toggled (not based on role restrictions)
    const toggleableLanes = userRole === 'owner'
        ? ['Done']
        : ['Unassigned', 'Done'];

    return (
        <div className="flex flex-col h-full">
            {/* Lane Toggle Bar */}
            <div className="flex items-center gap-2 px-4 md:px-10 pt-4 md:pt-6 pb-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Toggle Lanes:</span>
                {toggleableLanes.map(lane => (
                    <button
                        key={lane}
                        onClick={() => toggleLane(lane)}
                        className={cn(
                            "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wide transition-all",
                            hiddenLanes.has(lane)
                                ? "bg-slate-200 dark:bg-slate-800 text-slate-500 hover:bg-slate-300 dark:hover:bg-slate-700"
                                : "bg-siemens-petrol text-white"
                        )}
                    >
                        {hiddenLanes.has(lane) ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        {lane}
                        {lanes[lane]?.length > 0 && (
                            <span className="ml-1 bg-white/20 px-1.5 py-0.5 rounded-full">{lanes[lane].length}</span>
                        )}
                    </button>
                ))}
            </div>

            {/* Kanban Lanes */}
            <div className="flex flex-1 gap-4 md:gap-8 px-4 md:px-10 pb-4 overflow-x-auto snap-x snap-mandatory md:snap-none">
                {visibleLanes.map(lane => (
                    <KanbanLane
                        key={lane}
                        lane={lane as EpicLane}
                        items={lanes[lane] || []}
                        selectedId={selectedId}
                        onSelect={onSelect}
                        getPreviousScore={getPreviousScore}
                        getProjectName={getProjectName}
                        projects={projects}
                        onUpdate={onUpdate}
                        users={users}
                        userRole={userRole}
                    />
                ))}
            </div>
        </div>
    );
}

export default KanbanView;

